import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Filter, Loader2, RefreshCw, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { auditApi, type AuditFilterParams } from '@/api/audit';
import { unwrapError } from '@/api/client';
import { AUDIT_CHANNELS, type AuditChannel, type AuditRecord, type AuditStatus } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { JsonView } from '@/components/data/JsonView';
import { DataField } from '@/components/data/DataField';
import { LoadMore } from '@/components/data/LoadMore';
import { formatDate } from '@/lib/utils';
import { AUDIT_OUTCOMES, AUDIT_STATUS_GROUPS } from '@/components/audit/status-groups';
import { AuditStatusBadge } from '@/components/audit/AuditStatusBadge';
import { AuditJourney } from '@/components/audit/AuditJourney';
import { parsePrompt } from '@/components/audit/prompt';

interface Filters {
  username: string;
  intent: string;
  channel: AuditChannel | '';
  status: string;
  since: string;
  until: string;
}

const EMPTY_FILTERS: Filters = {
  username: '',
  intent: '',
  channel: '',
  status: '',
  since: '',
  until: '',
};

const LEGEND: Array<{ key: string; dot: string }> = [
  { key: 'resolved', dot: 'bg-emerald-500' },
  { key: 'escalated', dot: 'bg-amber-500' },
  { key: 'failed', dot: 'bg-destructive' },
  { key: 'pending', dot: 'bg-blue-500' },
  { key: 'closed', dot: 'bg-muted-foreground/40' },
];

function statusesForFilter(value: string): AuditStatus[] | undefined {
  if (value.startsWith('outcome:')) {
    return AUDIT_OUTCOMES.find((o) => `outcome:${o.key}` === value)?.statuses;
  }
  if (value.startsWith('status:')) return [value.slice('status:'.length) as AuditStatus];
  return undefined;
}

export function Audit(): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = React.useState<Filters>(EMPTY_FILTERS);
  const [openSessionId, setOpenSessionId] = React.useState<string | null>(null);

  const filterParams = React.useMemo<AuditFilterParams>(() => {
    const p: AuditFilterParams = {};
    if (applied.username) p.username = applied.username;
    if (applied.intent) p.intent = applied.intent;
    if (applied.channel) p.channel = applied.channel;
    const statuses = statusesForFilter(applied.status);
    if (statuses) p.status = statuses;
    if (applied.since) p.since = toIsoOrUndefined(applied.since);
    if (applied.until) p.until = toIsoOrUndefined(applied.until);
    return p;
  }, [applied]);

  const query = useInfiniteQuery({
    queryKey: ['audit', filterParams],
    queryFn: ({ pageParam }) =>
      auditApi.list({ ...filterParams, limit: 50, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items: AuditRecord[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  const exportMutation = useMutation({
    mutationFn: () => auditApi.exportCsv(filterParams),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `zorro-audit-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  function apply(): void {
    setApplied(filters);
  }
  function reset(): void {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  }
  const dirty = JSON.stringify(filters) !== JSON.stringify(applied);

  const loadedLabel = query.isLoading
    ? t('audit.loadedZero')
    : t('audit.loaded', { count: items.length });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('audit.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {loadedLabel}
            {query.hasNextPage ? t('audit.morePages') : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="gap-2"
          >
            {exportMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t('audit.exportCsv')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            className="gap-2"
          >
            <RefreshCw className={query.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            {t('common.refresh')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="h-4 w-4" />
            {t('audit.filtersTitle')}
          </div>
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
            <FilterText
              label={t('audit.username')}
              value={filters.username}
              onChange={(v) => setFilters({ ...filters, username: v })}
            />
            <FilterText
              label={t('audit.intent')}
              value={filters.intent}
              onChange={(v) => setFilters({ ...filters, intent: v })}
              placeholder={t('audit.intentPlaceholder')}
            />
            <div className="space-y-1.5">
              <Label className="text-xs">{t('audit.channel')}</Label>
              <Select
                value={filters.channel || '__any'}
                onValueChange={(v) =>
                  setFilters({
                    ...filters,
                    channel: v === '__any' ? '' : (v as AuditChannel),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">{t('common.any')}</SelectItem>
                  {AUDIT_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('audit.statusFilter.label')}</Label>
              <Select
                value={filters.status || '__any'}
                onValueChange={(v) => setFilters({ ...filters, status: v === '__any' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">{t('common.any')}</SelectItem>
                  <SelectGroup>
                    <SelectLabel>{t('audit.statusFilter.groups.overview')}</SelectLabel>
                    {AUDIT_OUTCOMES.map((o) => (
                      <SelectItem key={o.key} value={`outcome:${o.key}`}>
                        {t(`audit.statusFilter.outcomes.${o.key}`)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  {AUDIT_STATUS_GROUPS.map((g) => (
                    <SelectGroup key={g.key}>
                      <SelectLabel>{t(`audit.statusFilter.groups.${g.key}`)}</SelectLabel>
                      {g.statuses.map((st) => (
                        <SelectItem key={st} value={`status:${st}`}>
                          {t(`audit.status.${st}`)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FilterDate
              label={t('audit.since')}
              value={filters.since}
              onChange={(v) => setFilters({ ...filters, since: v })}
            />
            <FilterDate
              label={t('audit.until')}
              value={filters.until}
              onChange={(v) => setFilters({ ...filters, until: v })}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {LEGEND.map((l) => (
                <span key={l.key} className="inline-flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${l.dot}`} />
                  {t(`audit.legend.${l.key}`)}
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={reset} disabled={!dirty && isEmpty(applied)}>
                <X className="mr-1 h-3 w-3" />
                {t('common.reset')}
              </Button>
              <Button size="sm" onClick={apply} disabled={!dirty}>
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : query.error ? (
            <p className="p-6 text-sm text-destructive">{unwrapError(query.error)}</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">{t('audit.emptyForFilters')}</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('audit.tableHeader.when')}</TableHead>
                    <TableHead>{t('audit.tableHeader.user')}</TableHead>
                    <TableHead>{t('audit.tableHeader.request')}</TableHead>
                    <TableHead>{t('audit.tableHeader.status')}</TableHead>
                    <TableHead>{t('audit.tableHeader.channel')}</TableHead>
                    <TableHead className="text-right">{t('audit.tableHeader.tokens')}</TableHead>
                    <TableHead className="text-right">{t('audit.tableHeader.latency')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      key={row.sessionId}
                      className="cursor-pointer"
                      onClick={() => setOpenSessionId(row.sessionId)}
                    >
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-xs">{row.username}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {t('audit.bot')}: {row.botId.slice(0, 12)}…
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[360px]">
                        <span className="line-clamp-2 text-xs">{requestPreview(row)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <AuditStatusBadge status={row.status} />
                          {row.cached ? (
                            <Badge variant="outline" className="text-[10px]">
                              {t('audit.cachedBadge', { defaultValue: 'cache' })}
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.channel ? <Badge variant="outline">{row.channel}</Badge> : '—'}
                        <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">{row.intent ?? ''}</div>
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {(row.inputTokens ?? 0) + (row.outputTokens ?? 0)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {row.latencyMs ? `${row.latencyMs} ms` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                hasNext={query.hasNextPage ?? false}
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              />
            </>
          )}
        </CardContent>
      </Card>

      <AuditDetailDialog
        sessionId={openSessionId}
        onClose={() => setOpenSessionId(null)}
        onDeleted={() => {
          setOpenSessionId(null);
          void query.refetch();
        }}
      />
    </div>
  );
}

function FilterText({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 text-sm"
      />
    </div>
  );
}

function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 text-sm"
      />
    </div>
  );
}

function AuditDetailDialog({
  sessionId,
  onClose,
  onDeleted,
}: {
  sessionId: string | null;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['audit', 'detail', sessionId],
    queryFn: () => auditApi.get(sessionId!),
    enabled: sessionId !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => auditApi.delete(id),
    onSuccess: () => {
      toast.success(t('audit.deletedToast'));
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
      onDeleted();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const entry = detail.data?.entries[0];

  return (
    <Dialog open={sessionId !== null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {t('audit.detailTitle')}
            {entry ? <AuditStatusBadge status={entry.status} /> : null}
          </DialogTitle>
          <DialogDescription>
            {entry ? t(`audit.statusHint.${entry.status}`) : null}
          </DialogDescription>
        </DialogHeader>
        {detail.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : detail.error ? (
          <p className="text-sm text-destructive">{unwrapError(detail.error)}</p>
        ) : !entry ? (
          <p className="text-sm text-muted-foreground">{t('audit.noEntry')}</p>
        ) : (
          <div className="min-w-0 space-y-6 [overflow-wrap:anywhere]">
            <Section title={t('audit.sections.journey')}>
              <AuditJourney entry={entry} />
            </Section>
            <Section title={t('audit.sections.context')}>
              <div className="grid gap-3 md:grid-cols-3">
                <DataField label={t('audit.fields.created')} value={formatDate(entry.createdAt)} />
                <DataField label={t('audit.fields.updated')} value={formatDate(entry.updatedAt)} />
                <DataField label={t('audit.fields.intent')} value={entry.intent ?? '—'} mono />
                <DataField label={t('audit.fields.channel')} value={entry.channel ?? '—'} mono />
                <DataField label={t('audit.fields.origin')} value={entry.origin ?? '—'} mono />
                <DataField label={t('audit.fields.username')} value={entry.username} mono />
                <DataField label={t('audit.fields.userId')} value={entry.userId} mono />
                <DataField label={t('audit.fields.bot')} value={entry.botId} mono />
                <DataField label={t('audit.fields.proposal')} value={entry.proposalId ?? '—'} mono />
                <DataField label={t('audit.fields.zohoTicket')} value={entry.zohoTicketId ?? '—'} mono />
                <DataField label={t('audit.fields.session')} value={entry.sessionId} mono full />
                {entry.chatSessionId ? (
                  <DataField label={t('audit.fields.chatSession')} value={entry.chatSessionId} mono full />
                ) : null}
              </div>
            </Section>
            <details className="rounded-md border bg-muted/20 p-3">
              <summary className="cursor-pointer text-sm font-medium">{t('audit.sections.technical')}</summary>
              <div className="mt-3 space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <DataField label={t('audit.fields.llmProvider')} value={entry.llmProvider} />
                  <DataField label={t('audit.fields.llmModel')} value={entry.llmModel} mono />
                  <DataField
                    label={t('audit.fields.promptVersion')}
                    value={entry.promptVersion ? `v${entry.promptVersion}` : undefined}
                    mono
                  />
                  <DataField
                    label={t('audit.fields.tokens')}
                    value={`${entry.inputTokens ?? 0} / ${entry.outputTokens ?? 0}`}
                  />
                  <DataField label={t('audit.fields.latency')} value={entry.latencyMs ? `${entry.latencyMs} ms` : '—'} />
                  <DataField
                    label={t('audit.fields.cached')}
                    value={
                      entry.cached
                        ? t('audit.fields.cachedYes', { defaultValue: 'Served from cache' })
                        : t('audit.fields.cachedNo', { defaultValue: 'Full agent run' })
                    }
                  />
                </div>
                <Section title={t('audit.fields.prompt')}>
                  <JsonView value={entry.prompt} />
                </Section>
                {entry.reasoning ? (
                  <Section title={t('audit.fields.reasoning')}>
                    <pre className="max-h-[40vh] overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap">
                      {entry.reasoning}
                    </pre>
                  </Section>
                ) : null}
                {entry.toolCalls ? (
                  <Section title={t('audit.fields.toolCalls')}>
                    <JsonView value={entry.toolCalls} />
                  </Section>
                ) : null}
              </div>
            </details>
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => sessionId && deleteMutation.mutate(sessionId)}
            disabled={deleteMutation.isPending}
            className="gap-1.5"
          >
            <Trash2 className="h-3 w-3" />
            {t('audit.purgeEntry')}
          </Button>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function isEmpty(f: Filters): boolean {
  return !f.username && !f.intent && !f.channel && !f.status && !f.since && !f.until;
}

function requestPreview(row: AuditRecord): string {
  const { text, context } = parsePrompt(row.prompt);
  return text || context.map(([k, v]) => `${k}: ${v}`).join(' · ') || '—';
}

function toIsoOrUndefined(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}
